import { Box, Popover, Stack, styled, type PopoverOrigin } from '@mui/material';
import { Button, ListItem, vars } from '@tgtg/kit';
import {
  CountryCodeEnum,
  type CountryCode,
} from 'features/charity-vetting/types/charityVettingTypes';
import { useRef, useState } from 'react';
import intlResource from 'src/core/intl/intlResource';
import { type SupportedLocale } from 'src/core/intl/intlTypes';
import useLocaleChange from 'src/core/intl/useLocaleChange';
import ExpandMoreIcon from 'ui/SvgIcon/icons/ExpandMore';

const ANCHOR_ORIGIN: PopoverOrigin = { vertical: 'top', horizontal: 'center' };
const TRANSFORM_ORIGIN: PopoverOrigin = { vertical: 'bottom', horizontal: 'center' };

// The dropdown offers one language per launched vetting market, so the list stays
// in sync with the countries the flow supports (CountryCodeEnum).
const MARKET_LOCALES: Record<CountryCode, SupportedLocale> = {
  GB: 'en-GB',
  PT: 'pt-PT',
  ES: 'es-ES',
  PL: 'pl',
  FR: 'fr',
};

const AVAILABLE_LOCALES: SupportedLocale[] = Object.values(CountryCodeEnum).map(
  (countryCode) => MARKET_LOCALES[countryCode],
);

const supportedLanguages = intlResource.getSupportedLanguages();

const StyledOption = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})<{ isSelected: boolean }>(({ isSelected }) => ({
  ...(isSelected && {
    backgroundColor: vars.color.surface.interactive.opacityOverlayPressed,
  }),
}));

const StyledChevron = styled(ExpandMoreIcon, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>(({ theme: { transitions }, isOpen }) => ({
  transition: transitions.create('transform', { duration: transitions.duration.shortest }),
  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
}));

const LanguageSelect = () => {
  const { locale, changeLocale } = useLocaleChange();

  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = () => setIsOpen(true);
  const handleClose = () => setIsOpen(false);

  const handleSelect = (selectedLocale: SupportedLocale) => () => {
    changeLocale(selectedLocale);
    handleClose();
  };

  return (
    <Box ref={anchorRef}>
      <Button
        variant='transparent'
        onPress={handleOpen}
        trailingIcon={<StyledChevron isOpen={isOpen} />}
        aria-expanded={isOpen}
        style={{ color: vars.color.content.inverse }}
      >
        {supportedLanguages[locale]}
      </Button>
      <Popover
        open={isOpen}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={ANCHOR_ORIGIN}
        transformOrigin={TRANSFORM_ORIGIN}
        slotProps={{
          paper: {
            sx: {
              borderRadius: vars.radius.large,
              boxShadow: vars.elevation.regular,
              minWidth: anchorRef.current?.offsetWidth,
            },
          },
        }}
      >
        <Stack component='ul' sx={{ margin: 0, padding: 0, listStyle: 'none' }}>
          {AVAILABLE_LOCALES.map((availableLocale) => (
            <StyledOption
              key={availableLocale}
              isSelected={availableLocale === locale}
              id={`language-select-option-${availableLocale}`}
              label={supportedLanguages[availableLocale]}
              trailing={false}
              onPress={handleSelect(availableLocale)}
            />
          ))}
        </Stack>
      </Popover>
    </Box>
  );
};

export default LanguageSelect;
